import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import {useRouter} from 'next/router'
import type {FC} from 'react'
import {useCallback, useMemo} from 'react'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {AnimatedBackground} from 'slices/HeroV3/components/AnimatedBackground'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {LeadIdProvider} from 'slices/ProductTourSection/context/LeadIdContext'
import {Container} from 'src/common/Container'
import {LeadGenerationField} from 'src/common/LeadGenerationField'
import {AttributesSection} from './components/AttributesSection'
import {CountdownCounter} from './components/CountdownCounter'
import {CountdownTitle} from './components/CountdownTitle'
import {DescriptionSection} from './components/DescriptionSection'
import {HeadingSection} from './components/HeadingSection'
import {useTimeSavedCounter} from './useTimeSavedCounter'
import type {TimeValues} from './utils'

/**
 * Props for `TimeSavedSection`.
 */
export type TimeSavedSectionProps = SliceComponentProps<Content.TimeSavedSectionSlice> & {
  context: {pageUid: string}
}

/**
 * Component for "TimeSavedSection" Slices.
 */
const TimeSavedSection: FC<TimeSavedSectionProps> = ({slice, context}) => {
  const {primary} = slice

  // Prepare initial values from Prismic
  const timeSection = primary.time_saved_section[0]

  // Extract primitive values to ensure stable references
  const years = timeSection?.years || 0
  const months = timeSection?.months || 0
  const days = timeSection?.days || 0
  const hours = timeSection?.hours || 0
  const minutes = timeSection?.minutes || 0
  const startingDate = timeSection?.starting_date
  const growthRate = timeSection?.growth_rate
  const router = useRouter()
  const submitButton = isFilled.group(primary.lead_generation_input) && primary.lead_generation_input[0].submit_button
  const successLink = (submitButton && isFilled.link(submitButton) && submitButton.url) || '#'

  // Memoize initialValues to prevent infinite re-render loop
  const initialValues: TimeValues = useMemo(
    () => ({
      years,
      months,
      days,
      hours,
      minutes,
    }),
    [years, months, days, hours, minutes],
  )

  // Use the hook to calculate live values
  const {values} = useTimeSavedCounter({
    initialValues,
    startingDate,
    growthRate,
  })

  const handleSubmitSuccess = useCallback(() => {
    if (primary.lead_generation_input && primary.lead_generation_input[0]) {
      router.push(successLink)
    }
  }, [primary.lead_generation_input, router, successLink])

  return (
    <Container
      backgroundColor="Dark Green"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="!pb-0 !pt-8 text-moss-white md:!pb-0 md:!pt-20"
    >
      <div className="mx-auto w-full max-w-[1200px] px-5 xl:px-0">
        <div className="flex flex-col items-center gap-6 md:gap-8">
          <HeadingSection heading={primary.heading} />
          <AttributesSection attributes={primary.attributes} />
          <DescriptionSection description={primary.description} />
        </div>

        {/* Countdown Section */}
        {primary.time_saved_section && primary.time_saved_section[0] && (
          <div className="relative isolate flex flex-col items-center gap-8 overflow-hidden rounded-lg pb-16 pt-4 md:gap-16 md:pb-[128px] md:pt-8">
            <div className="isolate max-md:hidden">
              <AnimatedBackground gradientHeight={550} gradientStart={90} offsetTop={-200} />
            </div>
            <CountdownTitle title={primary.time_saved_section[0].title} />
            <CountdownCounter values={values} />

            {/* Lead Generation Field */}
            {primary.lead_generation_input && primary.lead_generation_input[0] && (
              <LeadIdProvider>
                <LeadGenerationField
                  pageUid={context.pageUid}
                  placeholder={primary.lead_generation_input[0].placeholder}
                  submitButtonText={
                    isFilled.link(primary.lead_generation_input[0].submit_button)
                      ? primary.lead_generation_input[0].submit_button.text
                      : null
                  }
                  buttonSize="medium"
                  onSuccess={handleSubmitSuccess}
                  tracking={{
                    recaptchaAction: 'time_saved_lead',
                    formName: 'time-saved-lead-form',
                    intent: 'time-saved-lead',
                    location: 'time-saved-section',
                  }}
                />
              </LeadIdProvider>
            )}
          </div>
        )}
      </div>
    </Container>
  )
}

export default TimeSavedSection
